import { reportMessageToSentry } from "@coworker/app/src/hooks/useConfigureSentry";
import useSaveGuard from "@coworker/apprestructured/src/save-guard/useSaveGuard";
import { merge } from "@coworker/apprestructured/src/shared/helpers/object.helper";
import useSearchParamState from "@coworker/apprestructured/src/shared/hooks/useSearchParamState";
import { NewOrExistingFile } from "@coworker/apprestructured/src/shared/types/newOrExistingFile";
import { FixaModal } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModal";
import { FixaModalHeader } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModalHeader";
import { FixaSheets } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaSheets";
import ScheduleSaveDecider, {
  ScheduleSaveDecision,
} from "@coworker/apprestructured/src/task-management/components/ScheduleSaveDeciderModal/ScheduleSaveDecider";
import EditTaskSchedule from "@coworker/apprestructured/src/task-management/components/ScheduleTask/EditTaskSchedule/EditTaskSchedule";
import {
  CreatorFragmentCommonProps,
  CreatorFragmentHandle,
} from "@coworker/apprestructured/src/task-management/components/TaskCreatorAndEditorSheet/creator-fragments/CreatorFragment";
import { TaskCreatorAndEditorSheetContent } from "@coworker/apprestructured/src/task-management/components/TaskCreatorAndEditorSheet/TaskCreatorAndEditorSheetContent/TaskCreatorAndEditorSheetContent";
import { TaskCreatorAndEditorSheetFooter } from "@coworker/apprestructured/src/task-management/components/TaskCreatorAndEditorSheet/TaskCreatorAndEditorSheetFooter/TaskCreatorAndEditorSheetFooter";
import {
  isLoading,
  LOADING,
} from "@coworker/apprestructured/src/task-management/loading";
import { invalidateTaskQueries } from "@coworker/apprestructured/src/task-management/services/queryKeyHelper";
import { useTask } from "@coworker/apprestructured/src/task-management/services/useTask";
import { useTaskFiles } from "@coworker/apprestructured/src/task-management/services/useTaskFiles";
import { useTaskImages } from "@coworker/apprestructured/src/task-management/services/useTaskImages";
import { useAllTaskTypes } from "@coworker/apprestructured/src/task-management/services/useTaskTypes";
import { useUpdatePendingTasksInSchedule } from "@coworker/apprestructured/src/task-management/services/useUpdatePendingTasksInSchedule";
import { useUpdateTask } from "@coworker/apprestructured/src/task-management/services/useUpdateTask";
import {
  PatchScheduleTask,
  PatchTask,
} from "@coworker/apprestructured/src/task-management/types/Task";
import {
  isNewUpload,
  NewUpload,
} from "@coworker/apprestructured/src/task-management/types/Upload";
import { useFileUploader } from "@coworker/apprestructured/src/uploader/useFileUploader";
import { useImageUploader } from "@coworker/apprestructured/src/uploader/useImageUploader";
import { useTranslation } from "@coworker/locales";
import { useQueryClient } from "@tanstack/react-query";
import {
  useCallback,
  useEffect,
  useEffectEvent,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTaskSchedule } from "../../../services/useTaskSchedule";
import { useNotificationsState } from "../../Notifications/hooks/useNotificationsState";
import { Notifications } from "../../Notifications/Notifications";
import { NotificationsProvider } from "../../Notifications/NotificationsContext";
import SomethingWentWrongPrompt from "../../SomethingWentWrongPrompt/SomethingWentWrongPrompt";
import styles from "./TaskEditorSheet.module.css";

const NO_IMAGES: NewOrExistingFile[] = [];
const NO_FILES: NewOrExistingFile[] = [];

/**
 * The TaskEditorSheet is open for the task with id 'taskEditor' taken from the url
 * search params. Open/close the sheet by adding/removing a 'taskEditor'
 * search param, or use the 'useSearchParamState' hook to reduce some boilerplate.
 */
function TaskEditorSheet() {
  const [{ taskEditor: taskId }, setSearchParams] = useSearchParamState();
  // Includes most unsaved changes the user has made to the task.
  const [taskChanges, setTaskChanges] = useState<Record<string, unknown>>({});
  // Images and files are handled separately because the data set when the user
  // makes changes is not the same as we send to the backend, so it cannot
  // be easily merged with the original task.
  // Is only undefined when the user has made no changes.
  const [patchTaskImages, setPatchTaskImages] = useState<
    NewOrExistingFile[] | undefined
  >(undefined);
  const [patchTaskFiles, setPatchTaskFiles] = useState<
    NewOrExistingFile[] | undefined
  >(undefined);
  const [isSaving, setIsSaving] = useState(false);
  const [showValidation, setShowValidation] = useState(false);

  const notifications = useNotificationsState();
  const { showError, clearAll } = notifications;

  const creatorFragmentRefs = useRef<Map<string, CreatorFragmentHandle>>(
    new Map()
  );

  const setCreatorFragmentRef = useCallback(
    (key: string) => (element: CreatorFragmentHandle | null) => {
      if (element) {
        creatorFragmentRefs.current.set(key, element);
      } else {
        creatorFragmentRefs.current.delete(key);
      }
    },
    []
  );
  const [scheduleSaveDeciderVisible, setScheduleSaveDeciderVisible] =
    useState(false);
  const [scheduleSaveDeciderDecision, setScheduleSaveDeciderDecision] =
    useState<ScheduleSaveDecision>("this");
  const { t } = useTranslation();

  const { mutateAsync: updateTask, error: updateTaskError } = useUpdateTask();
  const {
    mutateAsync: updatePendingTasksInSchedule,
    error: updatePendingTasksInScheduleError,
  } = useUpdatePendingTasksInSchedule();
  const { data: allTaskTypes, isLoading: isLoadingTaskTypes } =
    useAllTaskTypes();
  const { data: originalTask, isFetching: isFetchingOriginalTask } =
    useTask(taskId);
  const { data: originalImages } = useTaskImages(
    taskId,
    originalTask?.image_ids
  );
  const { data: originalFiles } = useTaskFiles(taskId, originalTask?.file_ids);

  const { data: schedule } = useTaskSchedule(taskId);

  const {
    ready: imageUploaderIsReady,
    upload: uploadImages,
    newOrExistingUploads: newOrExistingImageUploads,
    error: imageUploaderError,
  } = useImageUploader(patchTaskImages ?? NO_IMAGES);

  const {
    ready: fileUploaderIsReady,
    upload: uploadFiles,
    newOrExistingUploads: newOrExistingFileUploads,
    error: fileUploaderError,
  } = useFileUploader(patchTaskFiles ?? NO_FILES);

  // This is the body sent to the backend when saving and includes all changes.
  const patchTask = useMemo<PatchTask>(() => {
    const patchTask: PatchTask = { ...taskChanges };
    if (patchTaskImages !== undefined) {
      patchTask.images = newOrExistingImageUploads;
    }
    if (patchTaskFiles !== undefined) {
      patchTask.files = newOrExistingFileUploads;
    }
    return patchTask;
  }, [
    newOrExistingFileUploads,
    newOrExistingImageUploads,
    patchTaskFiles,
    patchTaskImages,
    taskChanges,
  ]);

  // Clear all changes when the task id changes in the url.
  const handleTaskIdChange = useEffectEvent(() => {
    clearAll();
    setTaskChanges({});
    setPatchTaskImages(undefined);
    setPatchTaskFiles(undefined);
    setIsSaving(false);
    setShowValidation(false);
  });

  useEffect(() => {
    handleTaskIdChange();
  }, [taskId]);

  const taskType = useMemo(() => {
    if (!allTaskTypes || !originalTask) {
      return undefined;
    }
    return allTaskTypes.find(({ id }) => id === originalTask.task_type_id);
  }, [originalTask, allTaskTypes]);

  const { enableSaveGuard, disableSaveGuard } = useSaveGuard();

  const handleScheduleSaveDeciderClose = useCallback(() => {
    setScheduleSaveDeciderVisible(false);
  }, []);

  const openScheduleSaveDecider = useCallback(() => {
    setScheduleSaveDeciderDecision("this");
    setScheduleSaveDeciderVisible(true);
  }, []);

  const queryClient = useQueryClient();

  const saveThisTask = useCallback(() => {
    setIsSaving(true);

    async function fn() {
      const [updateResult, imageUploadResult, fileUploadResult] =
        await Promise.allSettled([
          updateTask({
            id: taskId!,
            patchTask,
          }),
          uploadImages(),
          uploadFiles(),
        ]);
      handleImageUploadResult(
        imageUploadResult,
        newOrExistingImageUploads.filter(isNewUpload)
      );
      handleFileUploadResult(
        fileUploadResult,
        newOrExistingFileUploads.filter(isNewUpload)
      );

      if (imageUploadResult.status === "rejected") {
        showError({
          title: t("imageUploadFailedTitleString"),
          text: t("imageUploadFailedTextString"),
        });
      }
      if (fileUploadResult.status === "rejected") {
        showError({
          title: t("fileUploadFailedTitleString"),
          text: t("fileUploadFailedTextString"),
        });
      }
      if (updateResult.status === "fulfilled") {
        invalidateTaskQueries(queryClient);
        disableSaveGuard();
        setShowValidation(false);
        setSearchParams((prev) => {
          prev.delete("taskEditor");
          prev.set("taskViewer", String(updateResult.value["id"]));
        });
      }
    }

    fn();
  }, [
    disableSaveGuard,
    newOrExistingFileUploads,
    newOrExistingImageUploads,
    patchTask,
    queryClient,
    setSearchParams,
    showError,
    t,
    taskId,
    updateTask,
    uploadFiles,
    uploadImages,
  ]);

  const savePendingTasksInSchedule = useCallback(() => {
    setIsSaving(true);
    const patchScheduleTask = patchTaskToPatchScheduleTask(patchTask);

    async function fn() {
      const [updateResult, imageUploadResult, fileUploadResult] =
        await Promise.allSettled([
          updatePendingTasksInSchedule({
            scheduleId: originalTask!.schedule_id!,
            patchScheduleTask,
          }),
          uploadImages(),
          uploadFiles(),
        ]);
      handleImageUploadResult(
        imageUploadResult,
        newOrExistingImageUploads.filter(isNewUpload)
      );
      handleFileUploadResult(
        fileUploadResult,
        newOrExistingFileUploads.filter(isNewUpload)
      );
      if (imageUploadResult.status === "rejected") {
        showError({
          title: t("imageUploadFailedTitleString"),
          text: t("imageUploadFailedTextString"),
        });
      }
      if (fileUploadResult.status === "rejected") {
        showError({
          title: t("fileUploadFailedTitleString"),
          text: t("fileUploadFailedTextString"),
        });
      }
      if (updateResult.status === "fulfilled") {
        invalidateTaskQueries(queryClient);
        disableSaveGuard();
        setShowValidation(false);
        setSearchParams((prev) => {
          prev.delete("taskEditor");
          prev.set("taskViewer", taskId!);
        });
      }
    }

    fn();
  }, [
    disableSaveGuard,
    newOrExistingFileUploads,
    newOrExistingImageUploads,
    originalTask,
    patchTask,
    queryClient,
    setSearchParams,
    showError,
    t,
    taskId,
    updatePendingTasksInSchedule,
    uploadFiles,
    uploadImages,
  ]);

  const handleSaveDeciderConfirmClick = useCallback(() => {
    switch (scheduleSaveDeciderDecision) {
      case "this": {
        saveThisTask();
        break;
      }
      case "all": {
        savePendingTasksInSchedule();
        break;
      }
    }
    setScheduleSaveDeciderVisible(false);
  }, [savePendingTasksInSchedule, saveThisTask, scheduleSaveDeciderDecision]);

  const handleSaveClick = useCallback(() => {
    const someFragmentsAreInvalid = creatorFragmentRefs.current
      .values()
      .some((handle) => !handle.valid);

    if (someFragmentsAreInvalid) {
      setShowValidation(true);
      return;
    }

    if (Object.keys(patchTask).length === 0) {
      // We can skip saving if no changes are made
      setSearchParams((prev) => {
        prev.delete("taskEditor");
        prev.set("taskViewer", taskId!);
      });
    } else if (
      originalTask!.schedule_id === null ||
      originalTask!.state !== "pending" ||
      // We never want to bulk patch the start date
      Object.keys(patchTask).includes("start")
    ) {
      saveThisTask();
    } else {
      openScheduleSaveDecider();
    }
  }, [
    openScheduleSaveDecider,
    originalTask,
    patchTask,
    saveThisTask,
    setSearchParams,
    taskId,
  ]);

  const handleCloseClick = useCallback(() => {
    clearAll();
    setSearchParams((prev) => {
      prev.delete("taskEditor");
    });
  }, [clearAll, patchTask, setSearchParams, taskId]);

  const loadingFailed =
    !isFetchingOriginalTask &&
    !isLoadingTaskTypes &&
    (!originalTask || !taskType);

  const handleTaskChange = useCallback(
    (part: Record<string, unknown>) => {
      // The editor may still be mounted (closing animation, or a fragment
      // effect firing with a stale closure) after 'taskEditor' has already
      // been removed from the URL. Ignore changes reported once there is no
      // longer an active task id — they are not real user edits and must
      // not re-arm the save guard after it was disabled on close/save.
      if (!taskId) {
        return;
      }
      setTaskChanges((prev) => merge(prev, part));
      enableSaveGuard();
    },
    [enableSaveGuard, taskId]
  );

  const blockInput = isSaving || isFetchingOriginalTask || !taskId;

  const mergedTask = useMemo(() => {
    return originalTask ? merge(originalTask, patchTask) : LOADING;
  }, [originalTask, patchTask]);

  const creatorFragmentCommonProps: CreatorFragmentCommonProps = useMemo(() => {
    return {
      blockInput: blockInput,
      task: mergedTask,
      onTaskChange: handleTaskChange,
      showValidation,
      taskType: taskType ? taskType : LOADING,
    };
  }, [blockInput, mergedTask, handleTaskChange, showValidation, taskType]);

  const handleScheduleChange = useCallback(
    (start: string | null) => {
      handleTaskChange({ start });
    },
    [handleTaskChange]
  );

  const start = useMemo(() => {
    if (isLoading(mergedTask) || mergedTask["start"] === null) {
      return null;
    } else {
      return mergedTask["start"] as string;
    }
  }, [mergedTask]);

  const handleImagesChange = useCallback(
    (newImages: NewOrExistingFile[]) => {
      if (!taskId) {
        return;
      }
      enableSaveGuard();
      setPatchTaskImages(newImages);
    },
    [enableSaveGuard, taskId]
  );

  const handleFilesChange = useCallback(
    (newFiles: NewOrExistingFile[]) => {
      if (!taskId) {
        return;
      }
      enableSaveGuard();
      setPatchTaskFiles(newFiles);
    },
    [enableSaveGuard, taskId]
  );

  const images = patchTaskImages ?? originalImages ?? NO_IMAGES;
  const files = patchTaskFiles ?? originalFiles ?? NO_FILES;

  const handleRefreshClick = useCallback(() => {
    disableSaveGuard();
    setTimeout(() => {
      window.location.reload();
    }, 0);
  }, [disableSaveGuard]);

  const scheduleSaveDecider = useMemo(() => {
    return (
      <ScheduleSaveDecider
        visible={scheduleSaveDeciderVisible}
        decision={scheduleSaveDeciderDecision}
        onConfirmClick={handleSaveDeciderConfirmClick}
        onClose={handleScheduleSaveDeciderClose}
        onDecisionChange={setScheduleSaveDeciderDecision}
      />
    );
  }, [
    handleScheduleSaveDeciderClose,
    handleSaveDeciderConfirmClick,
    scheduleSaveDeciderDecision,
    scheduleSaveDeciderVisible,
  ]);

  const showSomethingWentWrongPrompt: boolean =
    !!updateTaskError ||
    !!updatePendingTasksInScheduleError ||
    !!imageUploaderError ||
    !!fileUploaderError;

  const scheduleComponent = useMemo(() => {
    return (
      <EditTaskSchedule
        disabled={blockInput}
        start={start}
        schedule={schedule}
        onConfirmClick={handleScheduleChange}
      />
    );
  }, [blockInput, handleScheduleChange, schedule, start]);

  return (
    <NotificationsProvider value={notifications}>
      <FixaModal
        data-testid={"task-editor-sheet"}
        visible={!!taskId}
        handleCloseBtn={handleCloseClick}
        onEscape={handleCloseClick}
      >
        <FixaSheets
          labelledById="task-management-task-editor-header"
          fullSize
          className={loadingFailed ? "" : styles["greyBackground"]}
          header={
            <FixaModalHeader
              data-testid={"task-editor-sheet-header"}
              id="task-management-task-editor-header"
              closeBtnClick={handleCloseClick}
              title={loadingFailed ? "" : t("editTaskString")}
            />
          }
          footer={
            loadingFailed ? null : (
              <TaskCreatorAndEditorSheetFooter
                testId={"task-editor-sheet-footer"}
                onClick={handleSaveClick}
                text={t("saveTaskString")}
                isLoading={isSaving}
                isDisabled={
                  !taskType ||
                  !originalTask ||
                  !taskId ||
                  !imageUploaderIsReady ||
                  !fileUploaderIsReady
                }
              />
            )
          }
        >
          <TaskCreatorAndEditorSheetContent
            isError={loadingFailed}
            errorTitle={t("sorryTaskCouldNotBeFoundString")}
            errorMessage={t("thisTaskDoesNotExistString")}
            creatorFragmentCommonProps={creatorFragmentCommonProps}
            isLoadingCreatorFragments={!taskType}
            detailsCreatorFragments={taskType ? taskType.creator_fragments : []}
            setCreatorFragmentRef={setCreatorFragmentRef}
            scheduleComponent={scheduleComponent}
            images={images}
            onImagesChange={handleImagesChange}
            isDisabled={blockInput}
            files={files}
            onFilesChange={handleFilesChange}
          />
        </FixaSheets>
      </FixaModal>
      {scheduleSaveDecider}
      {showSomethingWentWrongPrompt && (
        <SomethingWentWrongPrompt onRefreshClick={handleRefreshClick} />
      )}

      <Notifications />
    </NotificationsProvider>
  );
}

function handleImageUploadResult(
  imageUploadResult: PromiseSettledResult<void>,
  newImageUploads: NewUpload[]
) {
  if (imageUploadResult.status === "rejected") {
    const bucketIds = newImageUploads.map(
      ({ createUpload }) => createUpload.bucket_id
    );
    reportMessageToSentry("Image upload failed", { bucketIds }, "error");
  }
}

function handleFileUploadResult(
  fileUploadResult: PromiseSettledResult<void>,
  newFileUploads: NewUpload[]
) {
  if (fileUploadResult.status === "rejected") {
    const bucketIds = newFileUploads.map(
      ({ createUpload }) => createUpload.bucket_id
    );
    reportMessageToSentry("File upload failed", { bucketIds }, "error");
  }
}

// prettier-ignore
function patchTaskToPatchScheduleTask(patchTask: PatchTask): PatchScheduleTask {
  const patchScheduleTask: PatchScheduleTask = {};
  if (patchTask.priority !== undefined)
    patchScheduleTask.priority = patchTask.priority;
  if (patchTask.details !== undefined)
    patchScheduleTask.details = patchTask.details;
  if (patchTask.assigned_user_id !== undefined)
    patchScheduleTask.assigned_user_id = patchTask.assigned_user_id;
  if (patchTask.assigned_team_id !== undefined)
    patchScheduleTask.assigned_team_id = patchTask.assigned_team_id;
  if (patchTask.images !== undefined)
    patchScheduleTask.images = patchTask.images;
  if (patchTask.files !== undefined)
    patchScheduleTask.files = patchTask.files;
  if (patchTask.links !== undefined)
    patchScheduleTask.links = patchTask.links;
  return patchScheduleTask;
}

export default TaskEditorSheet;
